import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Waves, Sparkles, Bell } from "lucide-react";

export function Ambient() {
  const t = useT();
  const packs = useQuery({
    queryKey: ["ambient-packs"],
    queryFn: async () => (await api.get("/ambient/packs")).data,
    retry: false,
  });
  const loop = useQuery({
    queryKey: ["loop-state"],
    queryFn: async () => (await api.get("/loop/state")).data,
    retry: false,
  });
  const items = Array.isArray(packs.data) ? packs.data : packs.data?.packs || packs.data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.rhythm")} title="Ambient" subtitle="Textures for the day." />
      {loop.data && (
        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="loop-state">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">Loop</div>
          <div className="font-heading text-xl text-ink-900 mt-1">
            {loop.data?.state || loop.data?.phase || loop.data?.name || "steady"}
          </div>
          <p className="text-sm text-ink-500 mt-1">{loop.data?.description || loop.data?.hint || ""}</p>
        </div>
      )}
      {packs.isLoading && <LoadingCard />}
      {packs.isError && <ErrorState message={errMsg(packs.error)} onRetry={() => packs.refetch()} />}
      {!packs.isLoading && !packs.isError && items.length === 0 && (
        <EmptyState icon={Waves} title={t("common.empty")} testId="ambient-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ambient-packs">
        {items.map((p, i) => (
          <div key={p.id || i} className="rounded-2xl bg-white shadow-card p-4 hover:shadow-card-hover transition">
            <div className="text-sage-600 mb-1"><Sparkles className="h-4 w-4" strokeWidth={1.5} /></div>
            <div className="font-heading text-ink-900">{p.name || p.title || "Ambient pack"}</div>
            {p.description && <div className="text-sm text-ink-500 mt-1">{p.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Proactive() {
  const t = useT();
  const qc = useQueryClient();
  const pending = useQuery({
    queryKey: ["proactive-pending"],
    queryFn: async () => (await api.get("/proactive/pending")).data,
    retry: false,
  });
  const settings = useQuery({
    queryKey: ["proactive-settings"],
    queryFn: async () => (await api.get("/proactive/settings")).data,
    retry: false,
  });

  const items = Array.isArray(pending.data) ? pending.data : pending.data?.items || [];
  const enabled = !!(settings.data?.enabled ?? settings.data?.proactive ?? true);

  const engage = async (id) => {
    try { await api.post("/proactive/engage", { id }); toast.success("Engaged"); qc.invalidateQueries({ queryKey: ["proactive-pending"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dismiss = async (id) => {
    try { await api.post("/proactive/dismiss", { id }); qc.invalidateQueries({ queryKey: ["proactive-pending"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (v) => {
    try { await api.post("/proactive/settings", { enabled: v }); qc.invalidateQueries({ queryKey: ["proactive-settings"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Proactive support" title="Gentle nudges" subtitle="Small check-ins when Inner Compass senses you could use one." />
      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex items-center justify-between" data-testid="proactive-settings">
        <div>
          <div className="font-heading text-ink-900">Enable gentle nudges</div>
          <div className="text-sm text-ink-500">Turn off anytime.</div>
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="proactive-toggle" />
      </div>

      {pending.isLoading && <LoadingCard />}
      {!pending.isLoading && items.length === 0 && (
        <EmptyState icon={Bell} title="Nothing pending" description="You're steady — no nudges to open right now." testId="proactive-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="proactive-list">
        {items.map((p, i) => (
          <div key={p.id || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="text-sm text-ink-900">{p.text || p.message || p.title || "Gentle nudge"}</div>
            {p.created_at && <div className="text-xs text-ink-500 mt-1">{new Date(p.created_at).toLocaleString()}</div>}
            <div className="mt-3 flex gap-2">
              <button onClick={() => engage(p.id)} data-testid={`proactive-engage-${i}`} className="rounded-full border border-border h-8 px-3 text-xs hover:bg-cream-50">Engage</button>
              <button onClick={() => dismiss(p.id)} data-testid={`proactive-dismiss-${i}`} className="rounded-full border border-border h-8 px-3 text-xs hover:bg-cream-50 text-ink-500">Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
